import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSetApplicationStatus, useUpdateApplication } from "../../api/applications";
import { useToast } from "../../components/ToastProvider";
import { Application, ApplicationStatus } from "../../types/dtos";

const STATUS_OPTIONS: Array<{ value: ApplicationStatus; label: string }> = [
  { value: "ATIVO", label: "Ativo" },
  { value: "MANUTENCAO", label: "Em manutenção" },
  { value: "DESCONTINUADO", label: "Inativo (descontinuado)" },
];

export function EditApplicationModal({ application, onClose }: { application: Application | null; onClose: () => void }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState("");
  const [status, setStatus] = useState<ApplicationStatus>("ATIVO");
  const [contractObjectClause, setContractObjectClause] = useState("");
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateApplication();
  const setStatusMutation = useSetApplicationStatus();
  const toast = useToast();

  useEffect(() => {
    if (application) {
      setName(application.name);
      setCategory(application.category ?? "");
      setDescription(application.description ?? "");
      setVersion(application.version ?? "");
      setStatus(application.status);
      setContractObjectClause(application.contractObjectClause ?? "");
      setError(null);
    }
  }, [application]);

  if (!application) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return setError("Informe o nome da aplicação.");
    if (!application) return;

    try {
      await update.mutateAsync({ id: application.id, input: { name, category, description, version, contractObjectClause } });
      if (status !== application.status) {
        await setStatusMutation.mutateAsync({ id: application.id, status });
      }
      toast("Aplicação atualizada.");
      onClose();
    } catch {
      setError("Não foi possível salvar as alterações.");
    }
  }

  return (
    <Modal backdrop="static" keyboard={false} show onHide={onClose} centered className="nt-dialog" contentClassName="p-1">
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title style={{ fontSize: 20, fontFamily: "var(--font-heading)" }}>Editar aplicação</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <div className="field">
            <label className="form-label" style={{ fontSize: 12 }}>
              Nome
            </label>
            <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label className="form-label" style={{ fontSize: 12 }}>
              Categoria
            </label>
            <input className="form-control" value={category} onChange={(e) => setCategory(e.target.value)} />
          </div>
          <div className="field">
            <label className="form-label" style={{ fontSize: 12 }}>
              Descrição
            </label>
            <textarea className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label className="form-label" style={{ fontSize: 12 }}>
                Versão
              </label>
              <input className="form-control" value={version} onChange={(e) => setVersion(e.target.value)} />
            </div>
            <div className="col-12 col-sm-6">
              <label className="form-label" style={{ fontSize: 12 }}>
                Situação
              </label>
              <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value as ApplicationStatus)}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="form-label" style={{ fontSize: 12 }}>
              Cláusula do objeto do contrato (item 1.1 do PDF)
            </label>
            <textarea
              className="form-control"
              value={contractObjectClause}
              onChange={(e) => setContractObjectClause(e.target.value)}
              rows={4}
              placeholder="Texto que descreve o que o sistema faz, usado na cláusula 'OBJETO DO CONTRATO' do PDF gerado ao criar um contrato. Se vazio, usa um texto genérico."
            />
          </div>
          {error && (
            <div className="text-danger" style={{ fontSize: 12.5 }}>
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-success" disabled={update.isPending || setStatusMutation.isPending}>
            Salvar alterações
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
